import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, HeartHandshake, ShieldCheck, Info } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';

const POLL_MS = 45_000;

const ICON_BY_TYPE = { donation: HeartHandshake, security: ShieldCheck, system: Info };
const COLOR_BY_TYPE = { donation: '#d97757', security: '#5a8268', system: '#0e3a3a' };

function timeAgo(iso) {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const m = Math.floor(seconds / 60); if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24); return `il y a ${d} j`;
}

const isDev = process.env.NODE_ENV === 'development';

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications/mine', { params: { limit: 15 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) {
      if (isDev) console.warn('[NotificationsBell] load', e?.response?.status);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [user, load]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      if (isDev) console.warn('[NotificationsBell] markRead', e?.response?.status);
    }
  };

  const markAll = async () => {
    try {
      await api.post('/notifications/read-all');
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) {
      if (isDev) console.warn('[NotificationsBell] markAll', e?.response?.status);
    }
  };

  if (!user) return null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notifications-bell"
        aria-label={`${unread} notification(s) non lue(s)`}
        className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors"
      >
        <Bell className="w-4.5 h-4.5 text-[#1a1d1a]" />
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#d97757] text-white text-[10px] font-bold flex items-center justify-center leading-none"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 top-full mt-2 w-[360px] max-w-[92vw] rounded-2xl bg-white border border-black/10 shadow-2xl overflow-hidden z-50"
        >
          <div className="px-4 py-3 flex items-center justify-between border-b border-black/5">
            <div>
              <div className="text-[13.5px] font-semibold text-[#1a1d1a]">Notifications</div>
              <div className="text-[11px] text-neutral-500">{unread > 0 ? `${unread} non lue${unread > 1 ? 's' : ''}` : 'Tout est à jour'}</div>
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAll}
                data-testid="notifications-mark-all"
                className="text-[11.5px] text-[#d97757] hover:text-[#c66745] font-medium inline-flex items-center gap-1"
              >
                <Check className="w-3 h-3" /> Tout marquer lu
              </button>
            )}
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-6 py-10 text-center text-[13px] text-neutral-500">
                Aucune notification pour l'instant.<br />Elles apparaîtront ici dès que vous recevrez un don ou activerez la biométrie.
              </div>
            ) : (
              items.map((n) => {
                const Ico = ICON_BY_TYPE[n.type] || Info;
                const color = COLOR_BY_TYPE[n.type] || '#0e3a3a';
                const inner = (
                  <>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}18`, color }}>
                      <Ico className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#1a1d1a] truncate">{n.title}</div>
                      <div className="mt-0.5 text-[12px] text-neutral-600 line-clamp-2 leading-snug">{n.message}</div>
                      <div className="mt-1 text-[10.5px] text-neutral-400">{timeAgo(n.createdAt)}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#d97757] shrink-0 mt-1.5" />}
                  </>
                );
                const cls = `flex gap-3 px-4 py-3 border-b border-black/5 last:border-b-0 hover:bg-neutral-50 transition-colors ${n.read ? '' : 'bg-[#d97757]/5'}`;
                if (n.type === 'donation' && n.data?.cagnotteId) {
                  return (
                    <Link key={n.id} to={`/cagnotte/${n.data.cagnotteId}`} onClick={() => markRead(n.id)} className={cls}>
                      {inner}
                    </Link>
                  );
                }
                return (
                  <button key={n.id} type="button" onClick={() => markRead(n.id)} className={`w-full text-left ${cls}`}>
                    {inner}
                  </button>
                );
              })
            )}
          </div>
          <div className="px-4 py-2.5 border-t border-black/5 text-center">
            <Link to="/dashboard" onClick={() => setOpen(false)} className="text-[12px] text-neutral-600 hover:text-[#1a1d1a] font-medium">
              Ouvrir le tableau de bord →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
